'use client'

import { useEffect, useId, useRef, useState } from 'react'

import { ArrowLeft } from 'lucide-react'

import type { SearchWidgetCopy } from '@/lib/search-widget/copy'
import { cn } from '@/lib/utils'
import { usePlacesAutocomplete } from '@/hooks/use-places-autocomplete'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'

function LocationPinIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5 8.91765C5 5.09713 8.134 2 12 2C15.866 2 19 5.09713 19 8.91765C19 9.18348 18.981 9.44438 18.952 9.70132C18.458 16.276 12 22 12 22C12 22 5.542 16.276 5.048 9.70132C5.019 9.44438 5 9.18348 5 8.91765ZM12 12C13.6569 12 15 10.6569 15 9C15 7.34315 13.6569 6 12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12Z"
            />
        </svg>
    )
}

interface LocationComboboxProps {
    closeSuggestions: SearchWidgetCopy['closeSuggestions']
    label: string
    value: string
    onChange: (value: string, placeId: string) => void
    placeholder?: string
    hasError?: boolean
    grouped?: boolean
    /** Keep the grouped mobile presentation at every width (no md: borders). */
    forceMobile?: boolean
}

/** Shared prediction list used by both desktop popover and mobile overlay */
function PredictionList({
    predictions,
    isLoading,
    focusedIndex,
    onSelect,
    listboxId,
}: {
    predictions: { placeId: string; mainText: string; secondaryText: string; description: string }[]
    isLoading: boolean
    focusedIndex: number
    onSelect: (prediction: {
        placeId: string
        mainText: string
        secondaryText: string
        description: string
    }) => void
    listboxId?: string
}) {
    return (
        <>
            <ul role="listbox" id={listboxId} className="flex-1 overflow-auto py-1">
                {isLoading && predictions.length === 0 && (
                    <li className="px-3 py-2 text-sm text-[#8c939c]">Loading...</li>
                )}
                {predictions.map((prediction, i) => (
                    <li
                        key={prediction.placeId}
                        role="option"
                        aria-selected={i === focusedIndex}
                        id={`prediction-${prediction.placeId}`}
                    >
                        <button
                            type="button"
                            className={cn(
                                'flex w-full px-3 py-2 text-left',
                                i === focusedIndex ? 'bg-[#f5f6f7]' : 'hover:bg-[#f5f6f7]',
                            )}
                            onClick={() => onSelect(prediction)}
                        >
                            <span className="text-sm text-[#2e3033]">
                                {prediction.secondaryText
                                    ? `${prediction.mainText}, ${prediction.secondaryText}`.replace(
                                          /, USA$/,
                                          '',
                                      )
                                    : prediction.mainText}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
            {/* Google Places attribution (required by ToS) */}
            <div className="flex items-center justify-end border-t border-[#e8eaed] px-3 py-1.5">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png"
                    alt="Powered by Google"
                    className="h-[14px] w-auto"
                />
            </div>
        </>
    )
}

export function LocationCombobox({
    closeSuggestions,
    label,
    value,
    onChange,
    placeholder,
    hasError,
    grouped,
    forceMobile,
}: LocationComboboxProps) {
    const [open, setOpen] = useState(false)
    const [mobileOpen, setMobileOpen] = useState(false)
    const [input, setInput] = useState(value)
    const [mobileInput, setMobileInput] = useState('')
    const [focusedIndex, setFocusedIndex] = useState(-1)
    const inputRef = useRef<HTMLInputElement>(null)
    const mobileInputRef = useRef<HTMLInputElement>(null)
    const listboxId = useId()

    // Sync local input state when parent value changes (e.g., swap)
    useEffect(() => {
        setInput(value)
    }, [value])

    const { predictions, isLoading } = usePlacesAutocomplete(mobileOpen ? mobileInput : input)

    useEffect(() => {
        setFocusedIndex(-1)
    }, [predictions])

    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = 'hidden'
            return () => {
                document.body.style.overflow = ''
            }
        }
    }, [mobileOpen])

    useEffect(() => {
        if (mobileOpen) {
            requestAnimationFrame(() => {
                mobileInputRef.current?.focus()
            })
        }
    }, [mobileOpen])

    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        if (typeof window.matchMedia !== 'function') {
            return
        }
        const mql = window.matchMedia('(max-width: 767px)')
        setIsMobile(mql.matches)
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
        mql.addEventListener('change', handler)
        return () => mql.removeEventListener('change', handler)
    }, [])

    const handleSelect = (prediction: { placeId: string; description: string }) => {
        setInput(prediction.description)
        onChange(prediction.description, prediction.placeId)
        setOpen(false)
        setMobileOpen(false)
        setFocusedIndex(-1)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!open || predictions.length === 0) {
            return
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setFocusedIndex((i) => (i >= predictions.length - 1 ? 0 : i + 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setFocusedIndex((i) => (i <= 0 ? predictions.length - 1 : i - 1))
        } else if (e.key === 'Enter') {
            if (focusedIndex >= 0 && focusedIndex < predictions.length) {
                e.preventDefault()
                handleSelect(predictions[focusedIndex])
            }
        } else if (e.key === 'Escape') {
            e.preventDefault()
            setOpen(false)
            setFocusedIndex(-1)
        }
    }

    const handleInputFocus = () => {
        if (isMobile) {
            setMobileInput(input)
            setMobileOpen(true)
        } else if (predictions.length > 0) {
            setOpen(true)
        }
    }

    return (
        <>
            <Popover open={!isMobile && open && predictions.length > 0} onOpenChange={setOpen}>
                <PopoverAnchor asChild>
                    <div className="relative">
                        <LocationPinIcon className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-[#2e3033]" />
                        <input
                            ref={inputRef}
                            value={input}
                            onChange={(e) => {
                                const newValue = e.target.value
                                setInput(newValue)
                                // Typed text is not a selected place: the parent gets it with an empty placeId
                                onChange(newValue, '')
                                if (!isMobile) {
                                    setOpen(true)
                                }
                            }}
                            onFocus={handleInputFocus}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder ?? label}
                            aria-label={label}
                            role="combobox"
                            aria-controls={listboxId}
                            aria-expanded={!isMobile && open && predictions.length > 0}
                            aria-activedescendant={
                                focusedIndex >= 0 && predictions[focusedIndex]
                                    ? `prediction-${predictions[focusedIndex].placeId}`
                                    : undefined
                            }
                            readOnly={isMobile}
                            className={cn(
                                'h-12 w-full bg-white pr-3 pl-9 text-sm text-[#2e3033] placeholder:text-[#8c939c] focus:outline-none',
                                grouped
                                    ? cn(
                                          'border-0',
                                          !forceMobile &&
                                              cn(
                                                  'md:rounded-[7px] md:border md:focus:border-[#00bfd5]',
                                                  hasError
                                                      ? 'md:border-red-500'
                                                      : 'md:border-[#d9dee5]',
                                              ),
                                      )
                                    : cn(
                                          'rounded-[7px] border focus:border-[#00bfd5]',
                                          hasError ? 'border-red-500' : 'border-[#d9dee5]',
                                      ),
                            )}
                        />
                    </div>
                </PopoverAnchor>
                <PopoverContent
                    className="min-w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                    onOpenAutoFocus={(e) => {
                        e.preventDefault()
                    }}
                    // The input is the popover's *anchor*, not its trigger, so Radix counts
                    // focus or a pointer on it as "outside" and would dismiss the dropdown —
                    // including the focus a browser re-dispatches on the active element when
                    // its window regains activation.
                    onInteractOutside={(e) => {
                        if (inputRef.current?.contains(e.target as Node)) {
                            e.preventDefault()
                        }
                    }}
                >
                    <PredictionList
                        predictions={predictions}
                        isLoading={isLoading}
                        focusedIndex={focusedIndex}
                        onSelect={handleSelect}
                        listboxId={listboxId}
                    />
                </PopoverContent>
            </Popover>

            {/* Mobile full-screen overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 z-50 flex flex-col bg-white"
                    data-testid="location-overlay"
                >
                    <div className="flex items-center gap-2 border-b border-[#e8eaed] px-3 py-2">
                        <button
                            type="button"
                            onClick={() => setMobileOpen(false)}
                            aria-label={closeSuggestions.text}
                            className="flex h-10 w-10 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f5f6f7]"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <div className="relative flex-1">
                            <LocationPinIcon className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-[#2e3033]" />
                            <input
                                ref={mobileInputRef}
                                value={mobileInput}
                                onChange={(e) => setMobileInput(e.target.value)}
                                placeholder={placeholder ?? label}
                                aria-label={`Search ${label.toLowerCase()}`}
                                className="h-12 w-full rounded-[7px] border border-[#d9dee5] bg-white pr-3 pl-9 text-sm text-[#2e3033] placeholder:text-[#8c939c] focus:border-[#00bfd5] focus:outline-none"
                            />
                        </div>
                    </div>
                    <PredictionList
                        predictions={predictions}
                        isLoading={isLoading}
                        focusedIndex={-1}
                        onSelect={handleSelect}
                    />
                </div>
            )}
        </>
    )
}
